import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "./supabaseClient"

const V = "#7C3AED"
const V_SOFT = "#F3F0FF"
const V_MID = "#EDE9FE"
const WHITE = "#FFFFFF"
const OFF = "#FAFAFA"
const TEXT = "#09090B"
const MUTED = "#71717A"
const BORDER = "#E4E4E7"
const SUCCESS = "#059669"
const SUCCESS_BG = "#ECFDF5"
const SUCCESS_MID = "#D1FAE5"
const H = "'Plus Jakarta Sans', system-ui, sans-serif"
const B = "'Manrope', system-ui, sans-serif"


const MARKET_REACH = {
  es: [
    { id:"ciudad",    label:"Solo en mi ciudad",           sub:"Negocio local o presencial" },
    { id:"pais",      label:"En todo mi país",             sub:"A nivel nacional" },
    { id:"latam",     label:"Latinoamérica",               sub:"Varios países hispanohablantes" },
    { id:"espana",    label:"España",                      sub:"Mercado español" },
    { id:"us",        label:"Estados Unidos / Canadá",     sub:"Mercado norteamericano" },
    { id:"inter",     label:"Internacional",               sub:"Sin fronteras geográficas" },
  ],
  en: [
    { id:"ciudad",    label:"My city only",                sub:"Local or in-person business" },
    { id:"pais",      label:"My entire country",           sub:"Nationwide" },
    { id:"latam",     label:"Latin America",               sub:"Spanish-speaking countries" },
    { id:"espana",    label:"Spain",                       sub:"Spanish market" },
    { id:"us",        label:"United States / Canada",      sub:"North American market" },
    { id:"inter",     label:"International",               sub:"No geographic borders" },
  ]
}

const CLIENT_LOCATION = {
  es: [
    { id:"same",  label:"Mismo lugar que yo" },
    { id:"other", label:"Desde otras ciudades o países" },
    { id:"both",  label:"Ambos" },
  ],
  en: [
    { id:"same",  label:"Same place as me" },
    { id:"other", label:"From other cities or countries" },
    { id:"both",  label:"Both" },
  ]
}

const LOAD_PERSONAS = {
  es: ["Analizando tu mercado y ubicación...", "Construyendo perfiles de compradores reales...", "Aplicando contexto cultural y geográfico...", "Definiendo comportamientos de compra...", "Guardando los perfiles en tu proyecto..."],
  en: ["Analyzing your market and location...", "Building real buyer profiles...", "Applying cultural and geographic context...", "Defining purchasing behaviors...", "Saving profiles to your project..."],
}

const SECTIONS_ES = [
  {
    id:"identidad", label:"Identidad",
    title:"¿Quién eres?",
    sub:"Esta es la base de todo. Cada respuesta que escribas aquí queda guardada como la memoria de marketing de tu marca, y será la fuente de cada estrategia, contenido e imagen que KORIVA cree para ti. Tómate el tiempo que necesites. Vale la pena hacerlo bien.",
    fields:[
      { id:"nombre",    label:"Nombre del negocio",      type:"text",     placeholder:"Ej. Luna Estudio",                                          required:true },
      { id:"tipo",      label:"Tipo de negocio",         type:"select",
        options:[
          "Marca personal",
          "E-commerce / Productos físicos",
          "Servicio / Coach / Consultor",
          "Profesional independiente (médico, abogado, arquitecto, psicólogo, contador, dentista, etc.)",
          "Agencia / Estudio creativo",
          "Restaurante / Café / Local",
          "Educación / Cursos / Academia",
          "Tecnología / SaaS / App",
          "Salud y bienestar",
          "Moda / Belleza / Lifestyle",
          "Otro"
        ], required:true },
      { id:"origen",    label:"Origen del negocio",      type:"textarea", placeholder:"¿Cómo y por qué nació? La historia hace la marca.",         required:true },
    ]
  },
  {
    id:"producto", label:"Producto",
    title:"¿Qué ofreces?",
    sub:"Describe lo que vendes con claridad y detalle. Mientras más entienda KORIVA tu oferta real, más certera será toda la estrategia, los conceptos de imagen y las ideas de contenido. Porque hablarán exactamente del valor que tú entregas, no de generalidades.",
    fields:[
      { id:"producto",      label:"Producto o servicio principal", type:"textarea", placeholder:"Describe con detalle qué vendes y cómo funciona.",                        required:true },
      { id:"beneficios",    label:"Beneficios clave",              type:"textarea", placeholder:"¿Qué resultados o transformaciones obtiene el cliente?",                  required:true },
      { id:"diferenciador", label:"Diferenciador único",           type:"textarea", placeholder:"¿Qué hace que tu negocio sea diferente a todos los demás?",               required:true },
      { id:"precio_rango",  label:"Rango de precio",               type:"text",     placeholder:"Ej. $50 - $500 USD",                                                      required:false },
    ]
  },
  {
    id:"audiencia", label:"Audiencia",
    title:"¿A quién le hablas?",
    sub:"Cuanto más clara tengas tu audiencia, más poderoso será todo lo que KORIVA cree para ti. Diseñamos estrategias, contenidos y conceptos de imagen que conectan con tus clientes.",
    fields:[
      { id:"audiencia",   label:"Audiencia objetivo",              type:"textarea", placeholder:"Describe a tu cliente ideal: edad, género, ocupación, intereses.",        required:true },
      { id:"necesidad",   label:"Necesidad principal del usuario", type:"textarea", placeholder:"¿Qué problema real tiene tu cliente que tú resuelves?", required:true, explain:true },
      { id:"competencia", label:"Competencia principal",           type:"textarea", placeholder:"¿Quiénes son tus competidores? ¿Qué te diferencia de ellos?",            required:true },
    ]
  },
  {
    id:"mercado", label:"Mercado",
    title:"¿Dónde opera tu negocio?",
    sub:"El contexto geográfico y cultural cambia completamente quién es tu cliente ideal. Vender en Ciudad de México es muy distinto a vender en Miami o Madrid. KORIVA ajusta el lenguaje, los referentes y el tono según donde vivas.",
    fields:[], custom:"mercado"
  },
  {
    id:"marca", label:"Marca",
    title:"¿Cómo suenas?",
    sub:"Tu voz y tus valores definen el tono de todo lo que KORIVA genere para ti. Aquí construimos cómo suenas, para que cada palabra publicada se sienta tuya, no de una herramienta.",
    fields:[
      { id:"valores",         label:"Valores de marca",       type:"textarea", placeholder:"Ej. Autenticidad, innovación, cercanía, excelencia.",                       required:true },
      { id:"posicionamiento", label:"Posicionamiento",        type:"textarea", placeholder:"¿Cómo quieres que te recuerden? ¿Cuál es tu lugar en el mercado?",         required:true },
      { id:"ventajas",        label:"Ventajas competitivas",  type:"textarea", placeholder:"¿En qué eres objetivamente mejor que tu competencia?",                    required:true },
      { id:"objetivo_futuro", label:"Objetivo futuro",        type:"textarea", placeholder:"¿Cuál es la meta de tu negocio en los próximos 12 meses?",                required:true },
    ]
  },
  {
    id:"visual", label:"Logo",
    title:"Logo de tu marca",
    sub:"Sube tu logo para poder incluirlo en las imágenes que generes. El estilo visual de cada pieza lo eliges en el momento de crearla, no aquí. Así tienes libertad creativa por campaña.",
    fields:[], custom:"visual"
  },
  {
    id:"extra", label:"Extra",
    title:"Últimos detalles",
    sub:"Información adicional que ayuda a KORIVA a entender mejor tu negocio. Si tienes algo único, una historia, un detalle, una particularidad, cuéntalo aquí. Cuanto más sepamos, mejor te serviremos.",
    fields:[
      { id:"anos_mercado", label:"Años en el mercado",        type:"text",     placeholder:"Ej. 2 años",                                                              required:false },
      { id:"redes",        label:"Redes sociales activas",    type:"text",     placeholder:"Ej. Instagram, TikTok, LinkedIn",                                         required:false },
      { id:"bio_actual",   label:"Lo que dices de tu negocio en tus redes o web", type:"textarea", maxLength:800, placeholder:"Escribe o pega aqui lo que dice tu bio, algunos textos de tus publicaciones, o el titulo y descripcion de tu pagina web. Todo lo que ya comunicas de tu negocio nos ayuda a darte un mejor analisis.", required:true },
      { id:"web",          label:"Link del sitio web",        type:"text",     placeholder:"https://tudominio.com",                                                   required:false },
      { id:"info_adicional",    label:"Información adicional",     type:"textarea", placeholder:"Cualquier otro detalle que quieras que KORIVA conozca sobre tu negocio.", required:false },
    ]
  },
  {
    id:"sintesis", label:"Síntesis",
    title:"Misión y visión",
    sub:"Ahora que KORIVA conoce todo tu negocio, te ayudamos a crear tu misión y visión. Si ya las tienes, escríbelas. Si no, presiona el botón y las creamos juntos, basándonos en todo lo que ya nos contaste.",
    fields:[
      { id:"mision", label:"Misión", type:"textarea", placeholder:"¿Para qué existe tu negocio? En 1-2 oraciones.", required:false, helper:"mision" },
      { id:"vision", label:"Visión", type:"textarea", placeholder:"¿Hacia dónde va tu negocio en los próximos 5 años?", required:false, helper:"vision" },
    ]
  },
]

const SECTIONS_EN = [
  {
    id:"identity", label:"Identity",
    title:"Who are you?",
    sub:"This is the foundation of everything. Each answer you write here becomes your brand's marketing memory, and will be the source of every strategy, content and image KORIVA creates for you. Take the time you need. It's worth doing well.",
    fields:[
      { id:"nombre",    label:"Business name",  type:"text",     placeholder:"E.g. Luna Studio",  required:true },
      { id:"tipo",      label:"Business type",  type:"select",
        options:[
          "Personal brand",
          "E-commerce / Physical products",
          "Service / Coach / Consultant",
          "Independent professional (doctor, lawyer, architect, psychologist, accountant, dentist, etc.)",
          "Agency / Creative studio",
          "Restaurant / Café / Local business",
          "Education / Courses / Academy",
          "Technology / SaaS / App",
          "Health & wellness",
          "Fashion / Beauty / Lifestyle",
          "Other"
        ], required:true },
      { id:"origen",    label:"Business origin",type:"textarea", placeholder:"How and why was it born? The story makes the brand.", required:false },
    ]
  },
  {
    id:"product", label:"Product",
    title:"What do you offer?",
    sub:"Describe what you sell with clarity and detail. The more KORIVA understands your real offering, the more accurate the entire strategy, image concepts and content ideas will be. They'll speak directly to the value you deliver, not to generalities.",
    fields:[
      { id:"producto",      label:"Main product or service", type:"textarea", placeholder:"Describe in detail what you sell and how it works.",    required:true },
      { id:"beneficios",    label:"Key benefits",            type:"textarea", placeholder:"What results or transformations does your client get?", required:true },
      { id:"diferenciador", label:"Unique differentiator",   type:"textarea", placeholder:"What makes your business different from everyone else?",required:true },
      { id:"precio_rango",  label:"Price range",             type:"text",     placeholder:"E.g. $50 - $500 USD",                                   required:false },
    ]
  },
  {
    id:"audience", label:"Audience",
    title:"Who are you talking to?",
    sub:"The clearer your audience, the more powerful everything KORIVA creates for you. We design strategies, content and image concepts that connect with your clients.",
    fields:[
      { id:"audiencia",   label:"Target audience",     type:"textarea", placeholder:"Describe your ideal client: age, gender, occupation, interests.", required:true },
      { id:"necesidad",   label:"Main user need",      type:"textarea", placeholder:"What real problem does your client have that you solve?",         required:true, explain:true },
      { id:"competencia", label:"Main competition",    type:"textarea", placeholder:"Who are your competitors? What sets you apart from them?",        required:true },
    ]
  },
  {
    id:"market", label:"Market",
    title:"Where does your business operate?",
    sub:"Geographic and cultural context completely changes who your ideal client is. Selling in Mexico City is very different from selling in Miami or Madrid. KORIVA adjusts the language, references and tone based on where you live.",
    fields:[], custom:"mercado"
  },
  {
    id:"brand", label:"Brand",
    title:"How do you sound?",
    sub:"Your voice and values define the tone of everything KORIVA generates for you. Here we build how you sound, so every published word feels like yours, not like a tool's.",
    fields:[
      { id:"valores",         label:"Brand values",           type:"textarea", placeholder:"E.g. Authenticity, innovation, closeness, excellence.", required:true },
      { id:"posicionamiento", label:"Positioning",            type:"textarea", placeholder:"How do you want to be remembered? What's your market position?", required:true },
      { id:"ventajas",        label:"Competitive advantages", type:"textarea", placeholder:"What are you objectively better at than your competition?", required:false },
      { id:"objetivo_futuro", label:"Future goal",            type:"textarea", placeholder:"What is your business goal for the next 12 months?", required:false },
    ]
  },
  {
    id:"visual", label:"Logo",
    title:"Your logo",
    sub:"Upload your logo to include it in the images you generate. The visual style of each piece is chosen when creating it, not here. That way you have creative freedom per campaign.",
    fields:[], custom:"visual"
  },
  {
    id:"extra", label:"Extra",
    title:"Final details",
    sub:"Additional info that helps KORIVA understand your business better. If there's something unique, a story, a detail, a particularity, share it here. The more we know, the better we serve you.",
    fields:[
      { id:"anos_mercado", label:"Years in market",         type:"text",     placeholder:"E.g. 2 years",                                                      required:false },
      { id:"redes",        label:"Active social networks",  type:"text",     placeholder:"E.g. Instagram, TikTok, LinkedIn",                                  required:false },
      { id:"bio_actual",   label:"What you say about your business on social media or web", type:"textarea", maxLength:800, placeholder:"Write or paste here what your bio says, some text from your posts, or your website title and description. Everything you already communicate about your business helps us give you a better analysis.", required:true },
      { id:"web",          label:"Website link",            type:"text",     placeholder:"https://yourdomain.com",                                             required:false },
      { id:"info_adicional",    label:"Additional information",  type:"textarea", placeholder:"Any other details you want KORIVA to know about your business.",     required:false },
    ]
  },
  {
    id:"synthesis", label:"Synthesis",
    title:"Mission & vision",
    sub:"Now that KORIVA knows your full business, we help you create your mission and vision. If you already have them, write them. If not, click the button and we'll create them together, based on everything you've already told us.",
    fields:[
      { id:"mision", label:"Mission", type:"textarea", placeholder:"Why does your business exist? In 1-2 sentences.", required:false, helper:"mision" },
      { id:"vision", label:"Vision",  type:"textarea", placeholder:"Where is your business headed in the next 5 years?", required:false, helper:"vision" },
    ]
  },
]

const PERSONA_COLORS = [
  { bg:"#F3F0FF", border:"#EDE9FE", accent:V, label:"bg-v" },
  { bg:"#FFF0F6", border:"#FFD6EB", accent:"#BE185D", label:"bg-p" },
  { bg:"#F0FDF4", border:"#D1FAE5", accent:"#059669", label:"bg-g" },
]

export default function KorivaBrandForm() {
  const [lang, setLang]       = useState("es")
  const [section, setSection] = useState(0)
  const [data, setData]       = useState({})
  const [errors, setErrors]   = useState({})
  const [reach, setReach]     = useState(null)
  const [city, setCity]       = useState("")
  const [zones, setZones]     = useState("")
  const [clientLoc, setClientLoc] = useState(null)
  const [logo, setLogo]       = useState(null)
  const [logoName, setLogoName] = useState("")
  const [phase, setPhase]     = useState("form")
  const [personas, setPersonas] = useState([])
  const [brandVoice, setBrandVoice] = useState(null)
  const [aiVisibility, setAiVisibility] = useState(null)
  const [loadMsg, setLoadMsg] = useState(0)
  const [generatingHelper, setGeneratingHelper] = useState(null)
  const [helperResult, setHelperResult] = useState({ mision: null, vision: null })
  const [analyzingLogo, setAnalyzingLogo] = useState(false)
  const [logoColors, setLogoColors] = useState([])
  const [fontSuggestions, setFontSuggestions] = useState([])
  const [selectedFont, setSelectedFont] = useState(null)
  const [projectId, setProjectId] = useState(null)
  const [savingError, setSavingError] = useState("")
  const navigate = useNavigate()

  const es = lang === "es"
  const sections = es ? SECTIONS_ES : SECTIONS_EN
  const total    = sections.length
  const current  = sections[section]
  const reaches  = MARKET_REACH[lang]
  const clientLocs = CLIENT_LOCATION[lang]
  const loadMsgs = LOAD_PERSONAS[lang]

  useEffect(() => {
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Manrope:wght@300;400;500;600&display=swap"
    document.head.appendChild(link)
  }, [])

  useEffect(() => {
    const loadProject = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        navigate("/onboarding")
        return
      }
      // Leer el idioma guardado del usuario
      const { data: userData } = await supabase
        .from("users")
        .select("lang")
        .eq("id", session.user.id)
        .maybeSingle()
      if (userData && userData.lang) {
        setLang(userData.lang)
      }
      const { data: projects, error } = await supabase
        .from("projects")
        .select("id, nombre, tipo_negocio")
        .eq("user_id", session.user.id)
        .eq("activo", true)
        .order("created_at", { ascending: false })
        .limit(1)
        .single()
      if (error || !projects) {
        setSavingError("No se encontró tu proyecto. Vuelve al onboarding.")
        return
      }
      setProjectId(projects.id)
      const { data: bf, error: bfErr } = await supabase
        .from("brand_forms")
        .select("*")
        .eq("project_id", projects.id)
        .single()
      if (bf) {
        setData({
          nombre: projects.nombre || "",
          tipo: projects.tipo_negocio || "",
          origen: bf.origen || "",
          producto: bf.producto_principal || "",
          beneficios: bf.beneficios || "",
          diferenciador: bf.diferenciador || "",
          precio_rango: bf.rango_precio || "",
          audiencia: bf.audiencia || "",
          necesidad: bf.necesidad || "",
          competencia: bf.competencia || "",
          pais: bf.pais || "",
          nivel_economico: bf.nivel_economico || "",
          valores: bf.valores || "",
          posicionamiento: bf.posicionamiento || "",
          ventajas: bf.ventajas || "",
          objetivo_futuro: bf.objetivo_futuro || "",
          anos_mercado: bf.anos_mercado || "",
          redes_activas: bf.redes_activas || "",
          bio_actual: bf.bio_actual || "",
          sitio_web: bf.sitio_web || "",
          info_adicional: bf.info_adicional || "",
          mision: bf.mision || "",
          vision: bf.vision || ""
        })
        if (bf.alcance) setReach(bf.alcance)
        if (bf.ciudad) setCity(bf.ciudad)
        if (bf.donde_compra) setClientLoc(bf.donde_compra)
        if (bf.logo) setLogo(bf.logo)
        if (bf.logo_colores) { try { setLogoColors(JSON.parse(bf.logo_colores)) } catch(e){} }
        if (bf.fuente_elegida) setSelectedFont(bf.fuente_elegida)
      }
    }
    loadProject()
  }, [navigate])

  useEffect(() => {
    if (phase !== "generating_personas") return
    let i = 0
    const iv = setInterval(() => { i=(i+1)%loadMsgs.length; setLoadMsg(i) }, 900)
    return () => clearInterval(iv)
  }, [phase, es])

  const T = {
    es:{
      step:"Sección", of:"de", req:"Requerido", opt:"Opcional",
      next:"Continuar →", prev:"← Anterior",
      finish:"Guardar y generar perfiles de compradores →",
      mercadoQ1:"¿Dónde opera tu negocio?",
      mercadoQ2:"Especifica tu ciudad o zonas principales",
      mercadoQ2ph:"Ej. Ciudad de México, Monterrey, Guadalajara",
      mercadoQ3:"¿Tu cliente compra desde dónde?",
      mercadoReq:"Selecciona dónde opera tu negocio para continuar.",
      visualQ:"Elige el estilo visual de tu marca",
      visualSub:"Este estilo se aplicará automáticamente a todas las imágenes generadas. No tendrás que elegirlo cada semana.",
      logoQ:"Logo de tu proyecto", logoOpt:"Opcional — puedes subirlo después",
      logoUpload:"Subir logo", logoNote:"PNG, SVG · Fondo transparente recomendado",
      genTitle:"Generando tus perfiles de compradores",
      genSub:"KORIVA está creando perfiles culturalmente precisos para tu mercado.",
      personasTitle:"Cómo te lee la IA (Inteligencia Artificial) hoy",
      personasSub:"Analizamos lo que tu bio actual transmite y lo que se queda fuera. Por eso te sugerimos versiones mejoradas: para que la IA entienda rápido tu negocio y te recomiende a quien te busca.",
      descLabel:"Descripción",
      needLabel:"Necesidad principal",
      valLabel:"Qué valora",
      buyLabel:"Cómo decide comprar",
      objLabel:"Objeción principal",
      saveBtn:"Guardar perfiles y comenzar →",
      editBtn:"Editar un perfil",
      fillReq:"Completa los campos requeridos para continuar.",
      logoLoaded:"Logo cargado ✓",
    },
    en:{
      step:"Section", of:"of", req:"Required", opt:"Optional",
      next:"Continue →", prev:"← Back",
      finish:"Save and generate buyer profiles →",
      mercadoQ1:"Where does your business operate?",
      mercadoQ2:"Specify your city or main areas",
      mercadoQ2ph:"E.g. Miami, New York, Los Angeles",
      mercadoQ3:"Where does your client buy from?",
      mercadoReq:"Select where your business operates to continue.",
      visualQ:"Choose your brand's visual style",
      visualSub:"This style will be applied automatically to all generated images. You won't need to choose it every week.",
      logoQ:"Your project logo", logoOpt:"Optional — you can upload it later",
      logoUpload:"Upload logo", logoNote:"PNG, SVG · Transparent background recommended",
      genTitle:"Generating your buyer profiles",
      genSub:"KORIVA is creating culturally precise profiles for your market.",
      personasTitle:"How AI (Artificial Intelligence) reads you today",
      personasSub:"We analyzed what your current bio communicates and what's missing. That's why we suggest improved versions — so AI quickly understands your business and recommends you to the people searching for you.",
      descLabel:"Description",
      needLabel:"Main need",
      valLabel:"What they value",
      buyLabel:"How they decide to buy",
      objLabel:"Main objection",
      saveBtn:"Save profiles and start →",
      editBtn:"Edit a profile",
      fillReq:"Please complete the required fields to continue.",
      logoLoaded:"Logo loaded ✓",
    }
  }
  const t = T[lang]

  function update(id, val) {
    setData(d => ({...d,[id]:val}))
    setErrors(e => {const n={...e};delete n[id];return n})
  }

  function validate() {
    const errs = {}
    if (current.custom === "mercado") {
      if (!reach) errs["reach"] = true
    } else {
      current.fields.forEach(f => {
        if (f.required && !data[f.id]?.trim()) errs[f.id] = true
      })
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function next() {
    if (!validate()) return
    if (section < total - 1) setSection(s => s+1)
    else generatePersonas()
  }

  async function analyzeLogo(imageData) {
    setAnalyzingLogo(true)
    const tipoNegocio = data.tipo || data.nombre || "negocio"
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/analyze-logo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          image_base64: imageData.split(",")[1],
          tipo_negocio: tipoNegocio,
          lang: lang
        })
      })
      const parsed = await res.json()
      setLogoColors(parsed.colores || [])
      setFontSuggestions(parsed.fuentes || [])
      if (parsed.fuentes?.length > 0) setSelectedFont(parsed.fuentes[0].nombre)
    } catch(e) { console.error(e) }
    setAnalyzingLogo(false)
  }

  async function generateHelper(type) {
    setGeneratingHelper(type)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/generate-helper`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          type: type,
          lang: lang,
          nombre: data.nombre || "",
          tipo: data.tipo || "",
          producto: data.producto || "",
          audiencia: data.audiencia || "",
          valores: data.valores || "",
          diferenciador: data.diferenciador || "",
          posicionamiento: data.posicionamiento || "",
          mision: data.mision || "",
          objetivo_futuro: data.objetivo_futuro || ""
        })
      })
      const result = await res.json()
      const text = (result.text || "").trim()
      setHelperResult(prev => ({...prev,[type]:text}))
      update(type, text)
    } catch {}
    setGeneratingHelper(null)
  }

  async function generatePersonas() {
    setPhase("generating_personas"); setLoadMsg(0)
    setSavingError("")

    if (!projectId) {
      setSavingError("No se encontró tu proyecto. Vuelve al onboarding.")
      setPhase("form")
      return
    }

    // Si hay un logo NUEVO en base64 (data:), subirlo a R2 y obtener URL publica
    let logoUrlNueva = null
    if (logo && logo.startsWith("data:")) {
      try {
        const { data: { session: sessLogo } } = await supabase.auth.getSession()
        const upLogoRes = await fetch(process.env.REACT_APP_API_URL + "/api/upload-reference-photo", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${sessLogo?.access_token}` },
          body: JSON.stringify({ photo_base64: logo })
        })
        const upLogoData = await upLogoRes.json()
        if (upLogoRes.ok && upLogoData.storage_path) {
          logoUrlNueva = upLogoData.storage_path
          console.log("LOGO: subido a R2 OK, path =", logoUrlNueva)
        }
      } catch (logoErr) {
        console.error("LOGO ERROR al subir:", logoErr)
      }
    }

    const brandFormData = {
      project_id: projectId,
      logo: logo || null,
      logo_url: logoUrlNueva || null,
      logo_colores: logoColors.length ? JSON.stringify(logoColors) : null,
      fuente_elegida: selectedFont || null,
      origen: data.origen || null,
      producto_principal: data.producto || null,
      beneficios: data.beneficios || null,
      diferenciador: data.diferenciador || null,
      rango_precio: data.precio_rango || null,
      audiencia: data.audiencia || null,
      necesidad: data.necesidad || null,
      competencia: data.competencia || null,
      alcance: reach || null,
      pais: data.pais || null,
      ciudad: city || null,
      nivel_economico: data.nivel_economico || null,
      donde_compra: clientLoc || null,
      valores: data.valores || null,
      posicionamiento: data.posicionamiento || null,
      ventajas: data.ventajas || null,
      objetivo_futuro: data.objetivo_futuro || null,
      anos_mercado: data.anos_mercado || null,
      redes_activas: data.redes_activas || null,
      bio_actual: data.bio_actual || null,
      sitio_web: data.sitio_web || null,
      info_adicional: data.info_adicional || null,
      mision: data.mision || null,
      vision: data.vision || null
    }

    const { error: brandFormError } = await supabase
      .from("brand_forms")
      .upsert(brandFormData, { onConflict: "project_id" })

    if (brandFormError) {
      setSavingError("Error al guardar el formulario: " + brandFormError.message)
      setPhase("form")
      return
    }


    try {
      const { data: { session } } = await supabase.auth.getSession()
      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session?.access_token}`
      }

      // 1. Generar buyer personas (el backend genera Y guarda en buyer_personas)
      const resPersonas = await fetch(`${process.env.REACT_APP_API_URL}/api/generate-personas`, {
        method: "POST",
        headers,
        body: JSON.stringify({ project_id: projectId })
      })
      const personasData = await resPersonas.json()
      setPersonas(personasData || [])

      // 2. Generar voz de marca (el backend genera Y guarda en brand_voice)
      const resVoice = await fetch(`${process.env.REACT_APP_API_URL}/api/generate-voice`, {
        method: "POST",
        headers,
        body: JSON.stringify({ project_id: projectId })
      })
      const voiceData = await resVoice.json()
      setBrandVoice(voiceData || null)

      // 3. Analizar AI Visibility (como te lee la IA)
      const resAiVis = await fetch(`${process.env.REACT_APP_API_URL}/api/analyze-ai-visibility`, {
        method: "POST",
        headers,
        body: JSON.stringify({ project_id: projectId })
      })
      const aiVisData = await resAiVis.json()
      setAiVisibility(aiVisData || null)

      // 4. ADN visual (interno, el usuario no lo ve). Se lanza y no se espera.
      fetch(`${process.env.REACT_APP_API_URL}/api/generate-visual-dna`, {
        method: "POST",
        headers,
        body: JSON.stringify({ project_id: projectId })
      }).catch(() => {})

      setPhase("personas")
    } catch {
      setPhase("form")
    }
  }

  const progress = ((section + (phase==="done"?1:0)) / total) * 100

  const s = {
    page:      { minHeight:"100vh", background:WHITE, fontFamily:B, color:TEXT },
    header:    { borderBottom:`1px solid ${BORDER}`, padding:"1rem 2rem", display:"flex", alignItems:"center", justifyContent:"space-between" },
    logo:      { fontFamily:H, fontSize:17, fontWeight:800, color:TEXT, letterSpacing:"-.02em" },
    logoV:     { color:V },
    langRow:   { display:"flex", gap:3, background:OFF, borderRadius:8, padding:3, border:`1px solid ${BORDER}` },
    langBtn:   (a) => ({ background:a?V:"transparent", color:a?WHITE:MUTED, border:"none", borderRadius:6, padding:"4px 12px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:B, transition:"all .18s" }),
    progBar:   { height:3, background:BORDER },
    progFill:  { height:"100%", background:V, width:`${progress}%`, transition:"width .4s ease", borderRadius:"0 2px 2px 0" },
    layout:    { display:"grid", gridTemplateColumns:"200px 1fr", minHeight:"calc(100vh - 57px)" },
    sidebar:   { borderRight:`1px solid ${BORDER}`, padding:"1.75rem 1rem", background:OFF },
    sideTitle: { fontSize:11, fontWeight:700, letterSpacing:".12em", color:MUTED, textTransform:"uppercase", marginBottom:"1.25rem", fontFamily:B },
    sideItem:  (a, done) => ({ display:"flex", alignItems:"center", gap:9, padding:".55rem .7rem", borderRadius:8, marginBottom:4, cursor:done?"pointer":"default", background:a?V_SOFT:"transparent", transition:"background .18s" }),
    sideNum:   (a, done) => ({ width:22, height:22, borderRadius:"50%", background:done?V:(a?V_MID:BORDER), display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:done?WHITE:(a?V:MUTED), flexShrink:0 }),
    sideLbl:   (a) => ({ fontSize:14, fontWeight:a?700:500, color:a?V:MUTED, fontFamily:B }),
    main:      { padding:"2.5rem 3rem", maxWidth:900 },
    secLbl:    { fontSize:12, fontWeight:700, letterSpacing:".14em", color:V, textTransform:"uppercase", marginBottom:".5rem", fontFamily:B },
    secTitle:  { fontFamily:H, fontSize:34, fontWeight:800, color:TEXT, marginBottom:".5rem", letterSpacing:"-.03em", lineHeight:1.1 },
    secSub:    { fontSize:16, color:MUTED, lineHeight:1.65, marginBottom:"2rem", fontWeight:400 },
    fieldWrap: { marginBottom:"1.1rem" },
    fieldTop:  { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:5 },
    fieldLbl:  { fontSize:15, fontWeight:600, color:TEXT, fontFamily:B },
    badge:     (req) => ({ fontSize:10, fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", color:req?V:MUTED, background:req?V_SOFT:OFF, border:`1px solid ${req?V_MID:BORDER}`, borderRadius:5, padding:"3px 9px", fontFamily:B }),
    input:     (err) => ({ width:"100%", border:`1.5px solid ${err?"#EF4444":BORDER}`, background:WHITE, borderRadius:8, padding:"11px 14px", fontFamily:B, fontSize:15, color:TEXT, outline:"none", transition:"border .2s", boxSizing:"border-box", fontWeight:400 }),
    textarea:  (err) => ({ width:"100%", border:`1.5px solid ${err?"#EF4444":BORDER}`, background:WHITE, borderRadius:8, padding:"11px 14px", fontFamily:B, fontSize:15, color:TEXT, outline:"none", transition:"border .2s", boxSizing:"border-box", resize:"vertical", minHeight:86, lineHeight:1.65, fontWeight:400 }),
    select:    (err) => ({ width:"100%", border:`1.5px solid ${err?"#EF4444":BORDER}`, background:WHITE, borderRadius:8, padding:"11px 14px", fontFamily:B, fontSize:14, color:TEXT, outline:"none", transition:"border .2s", boxSizing:"border-box", cursor:"pointer", fontWeight:400 }),
    errMsg:    { fontSize:12, color:"#EF4444", marginTop:4, fontFamily:B },
    errBanner: { background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8, padding:"9px 13px", fontSize:12, color:"#DC2626", marginBottom:"1.25rem", fontFamily:B },
    navRow:    { display:"flex", gap:10, marginTop:"1.75rem" },
    btnPrimary:{ padding:"12px 22px", background:V, color:WHITE, border:"none", borderRadius:8, fontFamily:H, fontSize:14, fontWeight:700, cursor:"pointer", letterSpacing:"-.01em" },
    saveBtnFinal:{ padding:"15px 36px", background:V, color:WHITE, border:"none", borderRadius:10, fontFamily:H, fontSize:15, fontWeight:700, cursor:"pointer", letterSpacing:"-.01em", marginTop:"1.5rem", boxShadow:"0 2px 8px rgba(0,0,0,0.08)", transition:"all .2s" },
    btnSecond: { padding:"12px 18px", background:"transparent", color:MUTED, border:`1.5px solid ${BORDER}`, borderRadius:8, fontFamily:B, fontSize:13, fontWeight:500, cursor:"pointer" },
    // MARKET CUSTOM
    reachGrid: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:"1.25rem" },
    reachCard: (a) => ({ border:a?`2px solid ${V}`:`1.5px solid ${BORDER}`, background:a?V_SOFT:WHITE, borderRadius:10, padding:"1.1rem 1.25rem 1.1rem 1.35rem", cursor:"pointer", transition:"all .18s", position:"relative", overflow:"hidden" }),
    reachLine: (a) => ({ position:"absolute", left:0, top:0, bottom:0, width:3, background:a?V:BORDER }),
    reachLbl:  (a) => ({ fontFamily:H, fontSize:15, fontWeight:700, color:a?V:TEXT, marginBottom:3 }),
    reachSub:  { fontSize:13, color:MUTED, fontWeight:400 },
    fieldLblSm:{ fontSize:12, fontWeight:600, color:TEXT, marginBottom:5, display:"block", fontFamily:B },
    inputSm:   { width:"100%", border:`1.5px solid ${BORDER}`, background:WHITE, borderRadius:8, padding:"10px 13px", fontFamily:B, fontSize:13, color:TEXT, outline:"none", marginBottom:"1.1rem", boxSizing:"border-box", transition:"border .2s" },
    clientGrid:{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:"1.5rem" },
    clientCard:(a) => ({ border:a?`2px solid ${V}`:`1.5px solid ${BORDER}`, background:a?V_SOFT:WHITE, borderRadius:8, padding:"1rem 1.25rem", cursor:"pointer", fontFamily:B, fontSize:14, color:a?V:MUTED, fontWeight:a?700:500, transition:"all .18s", textAlign:"center" }),
    // VISUAL CUSTOM
    estiloGrid:{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:"1.5rem" },
    estiloCard:(a) => ({ border:a?`2px solid ${V}`:`1.5px solid ${BORDER}`, background:a?V_SOFT:WHITE, borderRadius:10, padding:".75rem .85rem", cursor:"pointer", transition:"all .18s" }),
    estiloName:(a) => ({ fontFamily:H, fontSize:13, fontWeight:700, color:a?V:TEXT, marginBottom:2 }),
    estiloDesc:{ fontSize:11, color:MUTED, fontWeight:400 },
    logoBox:   { border:`1.5px dashed ${BORDER}`, borderRadius:10, padding:"1.5rem", display:"flex", alignItems:"center", gap:"1.25rem", background:OFF, cursor:"pointer", marginTop:".75rem" },
    logoPreview:{ width:160, height:160, borderRadius:12, background:WHITE, border:`1px solid ${BORDER}`, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", flexShrink:0 },
    logoUpBtn: { background:V_SOFT, color:V, border:`1px solid ${V_MID}`, borderRadius:8, padding:"10px 16px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:B },
    // LOADING
    loadWrap:  { minHeight:"70vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"1.25rem" },
    spinner:   { width:48, height:48, border:`3px solid ${BORDER}`, borderTop:`3px solid ${V}`, borderRadius:"50%", animation:"spin 1s linear infinite" },
    loadTitle: { fontFamily:H, fontSize:20, fontWeight:800, color:TEXT, textAlign:"center", letterSpacing:"-.02em" },
    loadTxt:   { fontSize:13, color:MUTED },
    // PERSONAS
    personasWrap:{ maxWidth:900, margin:"0 auto", padding:"2.5rem 1.5rem" },
    personaCard: (idx) => ({ border:`1.5px solid ${PERSONA_COLORS[idx%3].border}`, background:PERSONA_COLORS[idx%3].bg, borderRadius:14, padding:"1.5rem", marginBottom:"1.25rem" }),
    personaHeader:{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:"1rem" },
    personaName: (idx) => ({ fontFamily:H, fontSize:24, fontWeight:800, color:PERSONA_COLORS[idx%3].accent, letterSpacing:"-.02em" }),
    personaMeta: { fontSize:14, color:MUTED, marginTop:3 },
    personaGrid: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1rem" },
    personaBlock:{ background:"rgba(255,255,255,.7)", borderRadius:10, padding:".85rem 1rem" },
    personaBlockFull:{ background:"rgba(255,255,255,.7)", borderRadius:10, padding:".85rem 1rem", gridColumn:"1/-1" },
    personaLbl: (idx) => ({ fontSize:11, fontWeight:700, letterSpacing:".12em", color:PERSONA_COLORS[idx%3].accent, textTransform:"uppercase", marginBottom:".5rem", fontFamily:B }),
    personaTxt: { fontSize:16, color:TEXT, lineHeight:1.7, fontWeight:400 },
    helperBtn:  { background:V_SOFT, border:`1.5px solid ${V_MID}`, color:V, borderRadius:8, padding:"10px 18px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:B, display:"flex", alignItems:"center", gap:6, transition:"all .18s", marginTop:8 },
    helperSpinner:{ width:12, height:12, border:`2px solid ${V_MID}`, borderTop:`2px solid ${V}`, borderRadius:"50%", animation:"spin .8s linear infinite", display:"inline-block" },
    helperResult:{ background:V_SOFT, border:`1px solid ${V_MID}`, borderRadius:8, padding:"8px 12px", fontSize:12, color:TEXT, lineHeight:1.6, marginTop:6, fontStyle:"italic" },
    explainBox: { background:V_SOFT, border:`1px solid ${V_MID}`, borderRadius:10, padding:"14px 18px", fontSize:13, color:TEXT, lineHeight:1.7, marginBottom:10, fontFamily:B },
    explainTitle:{ fontWeight:700, marginBottom:6, fontSize:13, color:V, fontFamily:H, letterSpacing:"-.01em" },
    voiceCard:  { background:"#F0FDF4", border:`1px solid ${SUCCESS_MID}`, borderRadius:14, padding:"1.25rem 1.4rem", marginBottom:"1.25rem" },
    voiceLbl:   { fontSize:12, fontWeight:700, letterSpacing:".14em", color:SUCCESS, textTransform:"uppercase", marginBottom:".75rem", fontFamily:B },
    voiceGrid:  { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1rem" },
    voiceBlock: { background:"rgba(255,255,255,.7)", borderRadius:8, padding:".75rem .85rem" },
    voiceBlockFull:{ background:"rgba(255,255,255,.7)", borderRadius:8, padding:".75rem .85rem", gridColumn:"1/-1" },
    voiceItemLbl:{ fontSize:11, fontWeight:700, letterSpacing:".12em", color:SUCCESS, textTransform:"uppercase", marginBottom:".5rem", fontFamily:B },
    voiceItemTxt:{ fontSize:16, color:TEXT, lineHeight:1.7, fontWeight:400 },
    voiceExample:{ fontSize:17, color:TEXT, lineHeight:1.6, fontWeight:500, fontStyle:"italic" },
    savedBadge: { display:"flex", alignItems:"center", gap:6, background:SUCCESS_BG, border:`1px solid ${SUCCESS_MID}`, borderRadius:20, padding:"4px 12px", fontSize:11, color:SUCCESS, fontWeight:700, fontFamily:B },
  }

  const completedSections = sections.map((sec,i) => {
    if (sec.custom==="mercado") return !!reach
    return sec.fields.filter(f=>f.required).every(f=>data[f.id]?.trim())
  })

  if (phase==="generating_personas") {
    return (
      <div style={s.page}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}} @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@700;800&family=Manrope:wght@400;500&display=swap');`}</style>
        <div style={s.header}>
          <div style={s.logo}>KORIVA <span style={s.logoV}>Studio™</span></div>
        </div>
        <div style={s.loadWrap}>
          <div style={s.spinner}/>
          <div style={s.loadTitle}>{t.genTitle}</div>
          <div style={s.loadTxt}>{loadMsgs[loadMsg]}</div>
        </div>
      </div>
    )
  }

  if (phase==="personas" && personas.length > 0) {
    return (
      <div style={s.page}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=Manrope:wght@300;400;500;600&display=swap'); @keyframes fi{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}} .fi{animation:fi .4s ease}`}</style>
        <div style={s.header}>
          <div style={{display:"flex",alignItems:"center",gap:16}}>
            <div style={s.logo}>KORIVA <span style={s.logoV}>Studio™</span></div>
            <button onClick={()=>navigate("/dashboard")} style={{background:"none",border:"none",color:V,fontFamily:B,fontSize:13,fontWeight:700,cursor:"pointer",padding:"4px 0",display:"flex",alignItems:"center",gap:5}}>
              <span>←</span><span>{es ? "Mis proyectos" : "My projects"}</span>
            </button>
          </div>
          <div style={s.savedBadge}>✓ {es?"Perfiles generados":"Profiles generated"}</div>
        </div>
        <div style={s.personasWrap}>
          <div className="fi">
            <div style={{fontSize:12,fontWeight:700,letterSpacing:".14em",color:V,textTransform:"uppercase",marginBottom:".5rem",fontFamily:B}}>KORIVA Studio</div>
            <div style={{fontFamily:H,fontSize:34,fontWeight:800,color:TEXT,marginBottom:".5rem",letterSpacing:"-.03em",lineHeight:1.1}}>{t.personasTitle}</div>
            <p style={{fontSize:16,color:MUTED,lineHeight:1.65,marginBottom:"2rem",fontWeight:400}}>{t.personasSub}</p>

            {/* AI VISIBILITY */}
            {aiVisibility && aiVisibility.score != null && (
              <div style={{ background:"#FFFFFF", border:"1px solid #EDE9FE", borderRadius:16, padding:"1.5rem", marginBottom:"1.5rem", boxShadow:"0 2px 12px rgba(124,58,237,0.06)" }} className="fi">
                <div style={{ fontSize:12, fontWeight:700, letterSpacing:".14em", color:V, textTransform:"uppercase", marginBottom:"1.25rem", fontFamily:B }}>{es?"Cómo te lee la inteligencia artificial":"How AI reads you"}</div>

                <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:"1.25rem" }}>
                  <div style={{ flexShrink:0, width:120, height:120, borderRadius:"50%", background:`conic-gradient(${V} ${(aiVisibility.score||0)*3.6}deg, #EDE9FE 0deg)`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <div style={{ width:96, height:96, borderRadius:"50%", background:"#FFF", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                      <span style={{ fontFamily:H, fontSize:38, fontWeight:800, color:TEXT, lineHeight:1 }}>{aiVisibility.score}</span>
                      <span style={{ fontSize:12, color:MUTED, fontFamily:B, marginTop:2 }}>/100</span>
                    </div>
                  </div>
                  <div style={{ fontSize:17, color:TEXT, fontFamily:B, lineHeight:1.55, fontStyle:"italic" }}>{aiVisibility.sugerencias?.resumen || (es?"Análisis de claridad de tu marca.":"Brand clarity analysis.")}</div>
                </div>

                {aiVisibility.lo_claro && aiVisibility.lo_claro.length > 0 && (
                  <div style={{ marginBottom:"1.1rem" }}>
                    <div style={{ fontSize:15, fontWeight:700, color:"#16A34A", marginBottom:".6rem", fontFamily:B }}>{es?"Lo que se entiende claro":"What comes across clearly"}</div>
                    {aiVisibility.lo_claro.map((item,idx)=>(
                      <div key={idx} style={{ fontSize:16, color:TEXT, fontFamily:B, lineHeight:1.7, marginBottom:".6rem", paddingLeft:"1.4rem", position:"relative" }}><span style={{ position:"absolute", left:0, color:"#16A34A", fontWeight:700 }}>✓</span>{item}</div>
                    ))}
                  </div>
                )}

                {aiVisibility.lo_borroso && aiVisibility.lo_borroso.length > 0 && (
                  <div style={{ marginBottom:"1.1rem" }}>
                    <div style={{ fontSize:15, fontWeight:700, color:"#D97706", marginBottom:".6rem", fontFamily:B }}>{es?"Lo que todavía está borroso":"What is still unclear"}</div>
                    {aiVisibility.lo_borroso.map((item,idx)=>(
                      <div key={idx} style={{ fontSize:16, color:TEXT, fontFamily:B, lineHeight:1.7, marginBottom:".6rem", paddingLeft:"1.4rem", position:"relative" }}><span style={{ position:"absolute", left:0, color:"#D97706", fontWeight:700 }}>•</span>{item}</div>
                    ))}
                  </div>
                )}

                {aiVisibility.sugerencias?.versiones_mejoradas && aiVisibility.sugerencias.versiones_mejoradas.length > 0 && (
                  <div>
                    <div style={{ fontSize:17, fontWeight:700, color:V, marginBottom:".5rem", fontFamily:H, letterSpacing:"-.01em" }}>{es?"Te sugerimos estas BIOs para tu negocio":"We suggest these BIOs for your business"}</div>
                    <div style={{ fontSize:15, fontWeight:500, color:TEXT, marginBottom:"1.25rem", fontFamily:B, lineHeight:1.65 }}>{es?"Las escribimos a partir de tu análisis. Cuanto más clara sea tu BIO, mejor te lee la IA y mejor te encuentra quien te necesita.":"Written based on your analysis. The clearer your BIO, the better AI reads your business and recommends you to the right people."}</div>
                    {aiVisibility.sugerencias.versiones_mejoradas.map((v,idx)=>(
                      <div key={idx} style={{ background:"#F9F7FF", border:"1px solid #EDE9FE", borderRadius:10, padding:"1.1rem 1.25rem", marginBottom:".75rem" }}>
                        <div style={{ fontSize:16, color:TEXT, fontFamily:B, lineHeight:1.7, marginBottom:".75rem" }}>{v}</div>
                        <button onClick={()=>navigator.clipboard.writeText(v).catch(()=>{})} style={{ fontSize:12, fontWeight:600, color:V, background:"#FFF", border:`1px solid ${V}`, borderRadius:6, padding:"6px 14px", cursor:"pointer", fontFamily:B }}>{es?"Copiar":"Copy"}</button>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ fontSize:13, color:MUTED, fontStyle:"italic", fontFamily:B, marginTop:"1.25rem", lineHeight:1.6, borderTop:"1px solid #F3F0FF", paddingTop:"1rem" }}>{es?"Este análisis se basa en la información que nos compartiste de tu negocio y tus redes. Mientras más completa sea tu información, más preciso será tu resultado.":"This analysis is based on the information you shared about your business and social media. The more complete your information, the more accurate your result."}</div>
              </div>
            )}

            {/* BRAND VOICE */}
            {brandVoice && (
              <div style={s.voiceCard} className="fi">
                <div style={s.voiceLbl}>✓ {es?"Voz de marca generada por KORIVA":"Brand voice generated by KORIVA"}</div>
                <div style={s.voiceGrid}>
                  <div style={s.voiceBlock}>
                    <div style={s.voiceItemLbl}>{es?"Tono":"Tone"}</div>
                    <div style={s.voiceItemTxt}>{brandVoice.tono}</div>
                  </div>
                  <div style={s.voiceBlock}>
                    <div style={s.voiceItemLbl}>{es?"Evitar siempre":"Always avoid"}</div>
                    <div style={s.voiceItemTxt}>{brandVoice.evitar}</div>
                  </div>
                  <div style={s.voiceBlockFull}>
                    <div style={s.voiceItemLbl}>{es?"Estilo de comunicación":"Communication style"}</div>
                    <div style={s.voiceItemTxt}>{brandVoice.estilo}</div>
                  </div>
                  <div style={s.voiceBlockFull}>
                    <div style={s.voiceItemLbl}>{es?"Ejemplo de voz":"Voice example"}</div>
                    <div style={s.voiceExample}>"{brandVoice.ejemplo}"</div>
                  </div>
                </div>
              </div>
            )}

            <div style={{ fontSize:15, fontWeight:700, color:V, marginBottom:".35rem", marginTop:"2rem", fontFamily:H, letterSpacing:"-.01em" }}>{es?"Conoce a tus clientes ideales":"Meet your ideal customers"}</div>
            <div style={{ fontSize:15, fontWeight:500, color:TEXT, marginBottom:"1.5rem", fontFamily:B, lineHeight:1.65 }}>{es?"Estos perfiles representan a las personas que más necesitan lo que ofreces. Cuando los tienes claros, sabes a quién dirigirte y qué decirle en cada publicación, anuncio o historia.":"These profiles represent the people who need what you offer most. When you know them clearly, you know who to speak to and what to say in every post, ad, or story."}</div>

            {personas.map((p,i) => (
              <div key={i} style={s.personaCard(i)} className="fi">
                <div style={s.personaHeader}>
                  <div>
                    <div style={s.personaName(i)}>{p.nombre}</div>
                    <div style={s.personaMeta}>{p.edad} · {p.ocupacion} · {p.ubicacion}</div>
                  </div>
                  <div style={{fontSize:11,fontWeight:700,color:PERSONA_COLORS[i%3].accent,background:"rgba(255,255,255,.6)",borderRadius:20,padding:"3px 10px",fontFamily:B}}>
                    {es?"Perfil":"Profile"} {i+1}
                  </div>
                </div>

                <div style={s.personaGrid}>
                  <div style={s.personaBlockFull}>
                    <div style={s.personaLbl(i)}>{t.descLabel}</div>
                    <div style={s.personaTxt}>{p.descripcion}</div>
                  </div>
                  <div style={s.personaBlock}>
                    <div style={s.personaLbl(i)}>{t.needLabel}</div>
                    <div style={s.personaTxt}>{p.necesidad}</div>
                  </div>
                  <div style={s.personaBlock}>
                    <div style={s.personaLbl(i)}>{t.valLabel}</div>
                    <div style={s.personaTxt}>{p.que_valora}</div>
                  </div>
                  <div style={s.personaBlock}>
                    <div style={s.personaLbl(i)}>{t.buyLabel}</div>
                    <div style={s.personaTxt}>{p.como_decide}</div>
                  </div>
                  <div style={s.personaBlock}>
                    <div style={s.personaLbl(i)}>{t.objLabel}</div>
                    <div style={s.personaTxt}>{p.objecion}</div>
                  </div>
                </div>
              </div>
            ))}

            <button style={s.saveBtnFinal} onClick={()=>navigate("/weekly")}>{t.saveBtn}</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={s.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Manrope:wght@300;400;500;600&display=swap');
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fi{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        .fi{animation:fi .3s ease}
        input:focus,textarea:focus,select:focus{border-color:${V}!important}
      `}</style>

      <div style={s.header}>
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          <div style={s.logo}>KORIVA <span style={s.logoV}>Studio™</span></div>
          <button onClick={()=>navigate("/dashboard")} style={{background:"none",border:"none",color:V,fontFamily:B,fontSize:13,fontWeight:700,cursor:"pointer",padding:"4px 0",display:"flex",alignItems:"center",gap:5}}>
            <span>←</span><span>{es ? "Mis proyectos" : "My projects"}</span>
          </button>
        </div>
        <div style={s.langRow}>
          <button style={s.langBtn(es)} onClick={()=>setLang("es")}>ES</button>
          <button style={s.langBtn(!es)} onClick={()=>setLang("en")}>EN</button>
        </div>
      </div>
      <div style={s.progBar}><div style={s.progFill}/></div>

      <div style={s.layout}>
        {/* SIDEBAR */}
        <div style={s.sidebar}>
          <div style={s.sideTitle}>{t.step} {section+1} {t.of} {total}</div>
          {sections.map((sec,i) => {
            const isDone = completedSections[i] && i < section
            const isActive = i === section
            return (
              <div key={sec.id} style={s.sideItem(isActive,i<section)} onClick={()=>i<section&&setSection(i)}>
                <div style={s.sideNum(isActive,isDone)}>{isDone?"✓":<span>{i+1}</span>}</div>
                <span style={s.sideLbl(isActive)}>{sec.label}</span>
              </div>
            )
          })}
        </div>

        {/* MAIN */}
        <div style={s.main}>
          <div className="fi" key={section}>
            <div style={s.secLbl}>{current.label}</div>
            <div style={s.secTitle}>{current.title}</div>
            <p style={s.secSub}>{current.sub}</p>

            {Object.keys(errors).length>0 && <div style={s.errBanner}>{t.fillReq}</div>}

            {/* STANDARD FIELDS */}
            {!current.custom && current.fields.map(field=>(
              <div key={field.id} style={s.fieldWrap}>
                <div style={s.fieldTop}>
                  <label style={s.fieldLbl}>{field.label}</label>
                  <span style={s.badge(field.required)}>{field.required?t.req:t.opt}</span>
                </div>

                {/* EXPLAIN BOX — necesidad del usuario */}
                {field.explain && (
                  <div style={s.explainBox}>
                    <div style={s.explainTitle}>{es?"Antes de seguir, hablemos de esto":"Before moving on, let's talk about this"}</div>
                    {es
                      ? <span>La necesidad de tu cliente <strong>NO</strong> es lo que tú vendes — es el problema real que tu cliente tiene antes de conocerte. Es muy común confundir esto. <br/><strong>Por ejemplo:</strong> si eres fotógrafo de bodas, la necesidad de tu cliente no es <em>"quiero fotos"</em>, es <em>"no quiero que el día más importante de mi vida quede en imágenes que no me representan"</em>. Cuanto más específico, más poderoso será cada contenido que KORIVA genere para ti.</span>
                      : <span>Your client's need is <strong>NOT</strong> what you sell — it's the real problem your client has before knowing you. This confusion is very common. <br/><strong>For example:</strong> if you're a wedding photographer, the client's need isn't <em>"I want photos"</em>, it's <em>"I don't want the most important day of my life to be captured in images that don't represent who we are"</em>. The more specific, the more powerful every piece of content KORIVA generates for you.</span>
                    }
                  </div>
                )}

                {field.type==="text" && <input style={s.input(errors[field.id])} value={data[field.id]||""} onChange={e=>update(field.id,e.target.value)} placeholder={field.placeholder}/>}
                {field.id==="bio_actual" && <div style={{ fontSize:14, color:"#7C3AED", fontFamily:B, marginBottom:10, lineHeight:1.6, background:"#F5F3FF", padding:"12px 14px", borderRadius:8, borderLeft:"3px solid #7C3AED" }}>{es?"Esta es la informacion que la IA, Google y las redes sociales usan para entender quien eres. Si esta incompleta o vaga, el analisis sera menos preciso. Pega tu bio actual o como te presentas hoy en tus canales digitales.":"This is the information AI, Google and social media use to understand who you are. If it's incomplete or vague, the analysis will be less accurate. Paste your current bio or how you present yourself today on your digital channels."}</div>}
                {field.type==="textarea" && <textarea style={s.textarea(errors[field.id])} value={data[field.id]||""} onChange={e=>update(field.id,e.target.value)} placeholder={field.placeholder} maxLength={field.maxLength}/>}
                {field.type==="textarea" && field.maxLength && <div style={{ fontSize:11, color:"#9CA3AF", textAlign:"right", marginTop:2, fontFamily:B }}>{(data[field.id]||"").length}/{field.maxLength}</div>}
                {field.type==="select" && (
                  <select style={s.select(errors[field.id])} value={data[field.id]||""} onChange={e=>update(field.id,e.target.value)}>
                    <option value="">{es?"Selecciona una opción":"Select an option"}</option>
                    {field.options.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                )}

                {/* HELPER BUTTON — mission / vision */}
                {field.helper && !data[field.id]?.trim() && (
                  <button style={s.helperBtn} onClick={()=>generateHelper(field.helper)} disabled={generatingHelper===field.helper}>
                    {generatingHelper===field.helper
                      ? <><span style={s.helperSpinner}/>{es?"Generando...":"Generating..."}</>
                      : <>✨ {es?`No tengo una, ayúdame a crearla`:`I don't have one, help me create it`}</>
                    }
                  </button>
                )}
                {field.helper && helperResult[field.helper] && data[field.id]?.trim() && (
                  <div style={{fontSize:11,color:SUCCESS,marginTop:4,fontFamily:B}}>✓ {es?"Generada por KORIVA — puedes editarla arriba":"Generated by KORIVA — you can edit it above"}</div>
                )}

                {errors[field.id]&&<div style={s.errMsg}>{es?"Este campo es requerido":"This field is required"}</div>}
              </div>
            ))}

            {/* MARKET CUSTOM */}
            {current.custom==="mercado" && (
              <div>
                <label style={s.fieldLblSm}>{t.mercadoQ1} <span style={{color:"#EF4444"}}>*</span></label>
                <div style={s.reachGrid}>
                  {reaches.map(r=>(
                    <div key={r.id} style={s.reachCard(reach===r.id)} onClick={()=>setReach(r.id)}>
                      <div style={s.reachLine(reach===r.id)}/>
                      <div style={s.reachLbl(reach===r.id)}>{r.label}</div>
                      <div style={s.reachSub}>{r.sub}</div>
                    </div>
                  ))}
                </div>
                {errors["reach"]&&<div style={{...s.errMsg,marginTop:-8,marginBottom:12}}>{t.mercadoReq}</div>}

                {(reach==="ciudad"||reach==="pais") && (
                  <>
                    <label style={s.fieldLblSm}>{t.mercadoQ2}</label>
                    <input style={s.inputSm} value={city} onChange={e=>setCity(e.target.value)} placeholder={t.mercadoQ2ph}/>
                    {reach==="pais" && (
                      <>
                        <label style={s.fieldLblSm}>{es?"¿Zonas o ciudades específicas donde tienes más clientes?":"Specific zones or cities where you have more clients?"}</label>
                        <input style={s.inputSm} value={zones} onChange={e=>setZones(e.target.value)} placeholder={es?"Ej. Norte del país, zona metropolitana...":"E.g. Northern region, metro area..."}/>
                      </>
                    )}
                  </>
                )}

                <label style={{...s.fieldLblSm,marginBottom:8}}>{t.mercadoQ3}</label>
                <div style={s.clientGrid}>
                  {clientLocs.map(c=>(
                    <div key={c.id} style={s.clientCard(clientLoc===c.id)} onClick={()=>setClientLoc(c.id)}>{c.label}</div>
                  ))}
                </div>
              </div>
            )}

            {/* VISUAL CUSTOM */}
            {current.custom==="visual" && (
              <div>
                {/* LOGO UPLOAD */}
                <div style={{marginTop:"1.25rem"}}>
                  <div style={{fontSize:14,fontWeight:700,color:TEXT,marginBottom:".25rem",fontFamily:H}}>{t.logoQ}</div>
                  <p style={{fontSize:13,color:MUTED,marginBottom:".75rem"}}>{t.logoOpt}</p>
                  <div style={s.logoBox} onClick={()=>document.getElementById("logo-input").click()}>
                    <div style={s.logoPreview}>
                      {logo?<img src={logo} alt="logo" style={{width:"100%",height:"100%",objectFit:"contain"}}/>:<span style={{fontSize:18,opacity:.3}}>🖼</span>}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:16,color:TEXT,fontWeight:600,marginBottom:4}}>{logo?t.logoLoaded:(es?"Ningún archivo seleccionado":"No file selected")}</div>
                      <div style={{fontSize:13,color:MUTED}}>{t.logoNote}</div>
                    </div>
                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      {logo && (
                        <button style={{ ...s.logoUpBtn, background:"transparent", color:MUTED, border:`1px solid ${BORDER}` }} onClick={e=>{e.stopPropagation(); setLogo(null); setLogoName(""); setLogoColors([])}}>
                          {es?"Quitar":"Remove"}
                        </button>
                      )}
                      <button style={s.logoUpBtn} onClick={e=>{e.stopPropagation();document.getElementById("logo-input").click()}}>
                        {logo ? (es?"Cambiar logo":"Change logo") : t.logoUpload}
                      </button>
                    </div>
                    <input id="logo-input" type="file" accept=".png,.svg,.jpg,.jpeg" style={{display:"none"}} onChange={e=>{
                      const f=e.target.files[0]; if(!f){ e.target.value = ""; return }
                      setLogoName(f.name)
                      const r=new FileReader()
                      r.onload=ev=>{ setLogo(ev.target.result); analyzeLogo(ev.target.result) }
                      r.readAsDataURL(f)
                      e.target.value = ""
                    }}/>
                  </div>
                </div>

                {/* ANALYZING LOGO */}
                {analyzingLogo && (
                  <div style={{display:"flex",alignItems:"center",gap:8,background:V_SOFT,border:`1px solid ${V_MID}`,borderRadius:10,padding:"10px 14px",marginTop:"1rem"}}>
                    <div style={{width:14,height:14,border:`2px solid ${V_MID}`,borderTop:`2px solid ${V}`,borderRadius:"50%",animation:"spin .8s linear infinite",flexShrink:0}}/>
                    <span style={{fontSize:12,color:V,fontFamily:B}}>{es?"Analizando tu logo — extrayendo colores y sugiriendo tipografías...":"Analyzing your logo — extracting colors and suggesting fonts..."}</span>
                  </div>
                )}

                {/* COLORS EXTRACTED */}
                {logoColors.length > 0 && !analyzingLogo && (
                  <div style={{background:SUCCESS_BG,border:`1px solid ${SUCCESS_MID}`,borderRadius:10,padding:"1rem 1.1rem",marginTop:"1rem"}}>
                    <div style={{fontSize:10,fontWeight:700,letterSpacing:".12em",color:SUCCESS,textTransform:"uppercase",marginBottom:".6rem",fontFamily:B}}>
                      ✓ {es?"Colores extraídos de tu logo":"Colors extracted from your logo"}
                    </div>
                    <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                      {logoColors.map((c,i)=>(
                        <div key={i} style={{display:"flex",alignItems:"center",gap:6}}>
                          <div style={{width:28,height:28,borderRadius:"50%",background:c,border:`1px solid rgba(0,0,0,.1)`,flexShrink:0}}/>
                          <span style={{fontSize:11,color:MUTED,fontFamily:B}}>{c}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{fontSize:11,color:SUCCESS,marginTop:8,fontFamily:B}}>
                      {es?"Estos colores se aplicarán automáticamente en tus imágenes de texto.":"These colors will be applied automatically to your text images."}
                    </div>
                  </div>
                )}

                {/* FONT SUGGESTIONS */}
                {fontSuggestions.length > 0 && !analyzingLogo && (
                  <div style={{marginTop:"1rem"}}>
                    <div style={{fontSize:12,fontWeight:700,color:TEXT,marginBottom:".2rem",fontFamily:H}}>
                      {es?"Tipografías sugeridas para tu marca":"Suggested fonts for your brand"}
                    </div>
                    <p style={{fontSize:12,color:MUTED,marginBottom:".75rem"}}>
                      {es?"KORIVA eligió estas fuentes basándose en tu logo y tipo de negocio. Elige una.":"KORIVA chose these fonts based on your logo and business type. Choose one."}
                    </p>
                    <link href={`https://fonts.googleapis.com/css2?${fontSuggestions.map(f=>`family=${f.google.replace(/ /g,"+")}`).join("&")}&display=swap`} rel="stylesheet"/>
                    <div style={{display:"grid",gridTemplateColumns:"1fr",gap:8}}>
                      {fontSuggestions.map((f,i)=>(
                        <div key={i}
                          style={{border:`1.5px solid ${selectedFont===f.nombre?V:BORDER}`,background:selectedFont===f.nombre?V_SOFT:WHITE,borderRadius:10,padding:".85rem 1rem",cursor:"pointer",transition:"all .18s"}}
                          onClick={()=>setSelectedFont(f.nombre)}>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                            <div style={{fontSize:22,fontFamily:`'${f.google}', sans-serif`,color:selectedFont===f.nombre?V:TEXT,fontWeight:700,letterSpacing:"-.01em"}}>
                              {es?"Tu marca, tu estrategia.":"Your brand, your strategy."}
                            </div>
                            {selectedFont===f.nombre && <div style={{fontSize:10,fontWeight:700,color:V,background:V_MID,borderRadius:20,padding:"2px 8px",fontFamily:B,whiteSpace:"nowrap"}}>✓ {es?"Elegida":"Selected"}</div>}
                          </div>
                          <div style={{fontSize:11,fontWeight:600,color:selectedFont===f.nombre?V:TEXT,fontFamily:B}}>{f.nombre}</div>
                          <div style={{fontSize:11,color:MUTED,fontFamily:B}}>{f.descripcion}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={s.navRow}>
              {section>0&&<button style={s.btnSecond} onClick={()=>setSection(s=>s-1)}>{t.prev}</button>}
              <button style={s.btnPrimary} onClick={next}>
                {section===total-1?t.finish:t.next}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
